import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../config/firebase';
import {
  collection, query, where, onSnapshot, orderBy, limit, addDoc,
  serverTimestamp, doc, runTransaction, increment,
} from 'firebase/firestore';
import ChessNav from './ChessNav';
import ChessBoard from './ChessBoard';
import Icon from '../Shared/Icons';
import { C, FONT } from '../../utils/chessTheme';

const PLAYER_KEY = 'iyanna_chess_player';
function loadPlayer() {
  try { return JSON.parse(localStorage.getItem(PLAYER_KEY)) || null; } catch { return null; }
}
const LIKED_KEY = 'iyanna_liked_games';
function loadLiked() { try { return JSON.parse(localStorage.getItem(LIKED_KEY)) || {}; } catch { return {}; } }
function saveLiked(m) { try { localStorage.setItem(LIKED_KEY, JSON.stringify(m)); } catch {} }

// ── Live & Saved — reels feed ──────────────────────────────────────────
// Scroll like TikTok through games. LIVE games update move by move; SAVED
// games replay their moves automatically. Rail is minimal: moyo (like),
// coins (gift), maoni (comments), share. Double-tap the board for a burst
// of hearts. A subtle Iyanna watermark sits in the corner.
// None of this reaches the two players — their focus is protected.

function GameReel({ game: initialGame, viewer, onCoins }) {
  const navigate = useNavigate();
  const gameId = initialGame.id;
  // Each reel subscribes to its OWN game doc so live moves stream in
  // reliably (fen, status, moveHistory, cheerCount stay fresh),
  // independent of the parent list snapshot.
  const [game, setGame] = useState(initialGame);
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'chessGames', gameId), snap => {
      if (snap.exists()) setGame({ id: snap.id, ...snap.data() });
    });
    return unsub;
  }, [gameId]);
  const [floaters, setFloaters] = useState([]);
  const [likedMap, setLikedMap] = useState(loadLiked);
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftTarget, setGiftTarget] = useState('white');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [replayFen, setReplayFen] = useState(game.fen);
  const floatId = useRef(0);
  const lastTap = useRef(0);

  const liked = !!likedMap[gameId];

  // Saved-game replay: step through moveHistory on a loop
  useEffect(() => {
    if (game.status !== 'finished' || !game.moveHistory?.length) { setReplayFen(game.fen); return; }
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % (game.moveHistory.length + 1);
      const step = game.moveHistory[i - 1];
      setReplayFen(i === 0 ? game.startFen || game.moveHistory[0]?.fenBefore || game.fen : (step?.fen || game.fen));
    }, 1400);
    return () => clearInterval(id);
  }, [game.status, game.moveHistory, game.fen, game.startFen]);

  // Comments (only loaded when opened)
  useEffect(() => {
    if (!commentsOpen) return;
    const q = query(collection(db, 'chessGames', gameId, 'comments'), orderBy('at', 'desc'), limit(50));
    return onSnapshot(q, snap => setComments(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [commentsOpen, gameId]);

  function spawnHearts(n) {
    const items = [];
    for (let i = 0; i < n; i++) {
      const id = ++floatId.current;
      items.push({ id, left: 12 + Math.random() * 66, delay: Math.random() * 0.25 });
    }
    setFloaters(f => [...f, ...items]);
    items.forEach(it => setTimeout(() => setFloaters(f => f.filter(x => x.id !== it.id)), 2400));
  }

  async function toggleLike() {
    const now = { ...likedMap };
    if (liked) {
      // Un-like: remove the count I added
      delete now[gameId]; setLikedMap(now); saveLiked(now);
      try { await runTransaction(db, async tx => { tx.update(doc(db, 'chessGames', gameId), { cheerCount: increment(-1) }); }); } catch {}
    } else {
      now[gameId] = true; setLikedMap(now); saveLiked(now);
      spawnHearts(1);
      try { await runTransaction(db, async tx => { tx.update(doc(db, 'chessGames', gameId), { cheerCount: increment(1) }); }); } catch {}
    }
  }

  function onBoardTap() {
    const t = Date.now();
    if (t - lastTap.current < 300) {
      // double-tap: burst of hearts (full colour) + ensure liked once
      spawnHearts(14);
      if (!liked) toggleLike();
    }
    lastTap.current = t;
  }

  async function sendGift(amount) {
    if (!viewer) { flash('Ingia kwanza ili kutoa coins.'); return; }
    const target = giftTarget === 'white' ? game.white : game.black;
    if (!target?.id || target.isBot) { flash('Chagua mchezaji halisi.'); return; }
    if (target.id === viewer.id) { flash('Huwezi kujizawadia.'); return; }
    setBusy(true);
    try {
      await runTransaction(db, async tx => {
        const meRef = doc(db, 'chessPlayers', viewer.id);
        const meSnap = await tx.get(meRef);
        const myCoins = meSnap.exists() ? (meSnap.data().coins || 0) : 0;
        if (myCoins < amount) throw new Error('coins');
        tx.update(meRef, { coins: myCoins - amount });
        tx.set(doc(db, 'chessPlayers', target.id), { coins: increment(amount) }, { merge: true });
      });
      flash(`Umempa ${target.name} coins ${amount}!`);
      setGiftOpen(false);
      onCoins && onCoins(-amount);
    } catch (e) {
      flash(e.message === 'coins' ? 'Coins hazitoshi.' : 'Imeshindikana.');
    }
    setBusy(false);
  }

  async function sendComment() {
    const text = commentText.trim();
    if (!text) return;
    setCommentText('');
    try {
      await addDoc(collection(db, 'chessGames', gameId, 'comments'), {
        text, by: viewer?.name || 'Mtazamaji', at: serverTimestamp(),
      });
    } catch (e) { console.error(e); }
  }

  function share() {
    const url = `${window.location.origin}/chess/room/${gameId}`;
    if (navigator.share) navigator.share({ title: 'Iyanna Chess', url }).catch(() => {});
    else { navigator.clipboard?.writeText(url); flash('Link imenakiliwa!'); }
  }

  function flash(m) { setNote(m); setTimeout(() => setNote(''), 2400); }

  const isLive = game.status === 'active';
  const isFinished = game.status === 'finished';
  const isEndedUnsaved = isFinished && !game.isSaved && (!game.moveHistory || !game.moveHistory.length || !game.startFen);
  const isReplay = isFinished && game.isSaved;
  const shownFen = isLive ? game.fen : replayFen;
  const stateLabel = isLive ? 'LIVE' : isReplay ? 'REPLAY' : 'IMEKWISHA';
  const stateColor = isLive ? '#F87171' : isReplay ? '#34D399' : C.textFaint;
  const lastMove = game.moveHistory?.length
    ? { from: game.moveHistory[game.moveHistory.length - 1].from, to: game.moveHistory[game.moveHistory.length - 1].to }
    : null;
  const cheerTotal = game.cheerCount || 0;

  return (
    <div style={styles.reel}>
      <style>{`@keyframes iyHeart {
        0% { transform: translateY(0) scale(0.5); opacity: 0; }
        14% { transform: translateY(-26px) scale(1.25); opacity: 1; }
        100% { transform: translateY(-260px) scale(0.85); opacity: 0; }
      }`}</style>

      <div style={styles.playersBar}>
        <span style={{ ...styles.stateDot, background: stateColor }} />
        <span style={styles.tinyTag}>{stateLabel}</span>
        <span style={styles.playerName}>{game.white?.name || '—'}</span>
        <span style={styles.vsTag}>vs</span>
        <span style={styles.playerName}>{game.black?.name || '—'}</span>
      </div>

      <div style={styles.boardWrap} onClick={onBoardTap}>
        <ChessBoard fen={shownFen} interactive={false} lastMove={lastMove} />
        {isEndedUnsaved && (
          <div style={styles.endedOverlay}>
            <div style={styles.endedText}>Live imekwisha</div>
            <div style={styles.endedSub}>Mchezo huu haukurekodiwa</div>
          </div>
        )}
        <div style={styles.watermark}>Iyanna Chess</div>
        <div style={styles.floatLayer}>
          {floaters.map(f => (
            <div key={f.id} style={{ position: 'absolute', bottom: '8%', left: `${f.left}%`, animation: `iyHeart 2.3s ease-out ${f.delay}s forwards` }}>
              <Icon name="heart" size={30} color="#FF4D6D" strokeWidth={2} fill="#FF4D6D" />
            </div>
          ))}
        </div>
      </div>

      <div style={styles.rail}>
        <button style={styles.railBtn} onClick={toggleLike}>
          <Icon name="heart" size={24} color={liked ? '#FF4D6D' : '#fff'} strokeWidth={2} fill={liked ? '#FF4D6D' : 'none'} />
          <span style={styles.railCount}>{cheerTotal}</span>
        </button>
        <button style={styles.railBtn} onClick={() => setCommentsOpen(true)}>
          <Icon name="notes" size={22} color="#fff" strokeWidth={2} />
          <span style={styles.railLabel}>Maoni</span>
        </button>
        <button style={styles.railBtn} onClick={() => setGiftOpen(o => !o)}>
          <Icon name="coin" size={22} color="#FBBF24" strokeWidth={2} />
          <span style={styles.railLabel}>Coins</span>
        </button>
        <button style={styles.railBtn} onClick={share}>
          <Icon name="link" size={21} color="#fff" strokeWidth={2} />
          <span style={styles.railLabel}>Share</span>
        </button>
        {isLive ? (
          <button style={styles.railBtn} onClick={() => navigate(`/chess/room/${gameId}`)}>
            <Icon name="eye" size={21} color="#fff" strokeWidth={2} />
            <span style={styles.railLabel}>Ingia</span>
          </button>
        ) : (
          <button style={styles.railBtn} onClick={() => navigate(`/chess/review/${gameId}`)}>
            <Icon name="sparkle" size={21} color="#34D399" strokeWidth={2} />
            <span style={styles.railLabel}>Chambua</span>
          </button>
        )}
      </div>

      {giftOpen && (
        <div style={styles.sheet}>
          <div style={styles.sheetTitle}>Mpe coins nani?</div>
          <div style={styles.giftTargets}>
            {['white', 'black'].map(side => {
              const p = game[side];
              if (!p || p.isBot) return null;
              return (
                <button key={side} style={{ ...styles.giftTarget, ...(giftTarget === side ? styles.giftTargetActive : {}) }} onClick={() => setGiftTarget(side)}>
                  {p.name}
                </button>
              );
            })}
          </div>
          <div style={styles.giftAmounts}>
            {[1, 5, 10].map(a => (
              <button key={a} style={styles.giftAmount} disabled={busy} onClick={() => sendGift(a)}>
                <Icon name="coin" size={14} color="#FBBF24" /> {a}
              </button>
            ))}
          </div>
          <div style={styles.giftBalance}>Salio: {viewer?.coins ?? 0}</div>
        </div>
      )}

      {commentsOpen && (
        <div style={styles.commentsOverlay} onClick={() => setCommentsOpen(false)}>
          <div style={styles.commentsSheet} onClick={e => e.stopPropagation()}>
            <div style={styles.commentsHandle} />
            <div style={styles.commentsTitle}>Maoni</div>
            <div style={styles.commentsList}>
              {comments.length === 0 ? (
                <div style={styles.commentsEmpty}>Kuwa wa kwanza kutoa maoni.</div>
              ) : comments.map(c => (
                <div key={c.id} style={styles.commentRow}>
                  <span style={styles.commentBy}>{c.by}</span>
                  <span style={styles.commentText}>{c.text}</span>
                </div>
              ))}
            </div>
            <div style={styles.commentInputRow}>
              <input
                style={styles.commentInput}
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendComment()}
                placeholder="Andika maoni..."
                maxLength={140}
              />
              <button style={styles.commentSend} onClick={sendComment}>
                <Icon name="play" size={16} color="#06281A" strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      )}

      {note && <div style={styles.note}>{note}</div>}
    </div>
  );
}

export default function ChessWatch() {
  const navigate = useNavigate();
  const [viewer, setViewer] = useState(loadPlayer());
  const [live, setLive] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!viewer?.id) return;
    return onSnapshot(doc(db, 'chessPlayers', viewer.id), snap => {
      if (snap.exists()) setViewer(v => ({ ...v, coins: snap.data().coins || 0 }));
    });
  }, [viewer?.id]);

  useEffect(() => {
    // Single-field query — filter privacy on the client (no index needed)
    const qLive = query(collection(db, 'chessGames'), where('status', '==', 'active'));
    return onSnapshot(qLive, snap => {
      setLive(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(g => !g.isPrivate));
      setLoading(false);
    }, () => setLoading(false));
  }, []);

  useEffect(() => {
    // Saved replays: finished public games the players chose to save.
    // Single where (no index); newest-first sorting done on the client.
    const qSaved = query(collection(db, 'chessGames'), where('isSaved', '==', true));
    return onSnapshot(qSaved, snap => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.finishedAt?.seconds || 0) - (a.finishedAt?.seconds || 0))
        .slice(0, 20);
      setSaved(list);
    }, () => {});
  }, []);

  const feed = [...live, ...saved.filter(s => !live.find(l => l.id === s.id))];

  return (
    <div style={styles.page}>
      {feed.length === 0 && !loading ? (
        <div style={styles.centerBox}>
          <div style={styles.emptyCard}>
            <Icon name="play" size={30} color={C.textFaint} />
            <div style={styles.emptyText}>Hakuna mchezo wa kuangalia sasa.</div>
            <button style={styles.startBtn} onClick={() => navigate('/chess')}>Anzisha Mchezo</button>
          </div>
        </div>
      ) : (
        <div style={styles.feed}>
          {feed.map(g => (
            <div key={g.id} style={styles.snap}>
              <GameReel game={g} viewer={viewer} />
            </div>
          ))}
        </div>
      )}
      <ChessNav />
    </div>
  );
}

const styles = {
  page: { height: '100vh', background: C.bgGradient, overflow: 'hidden', position: 'relative' },
  feed: { height: '100vh', overflowY: 'auto', scrollSnapType: 'y mandatory', paddingBottom: '0' },
  snap: { height: '100vh', scrollSnapAlign: 'start', scrollSnapStop: 'always' },
  reel: { height: '100%', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '46px 12px 12px' },
  playersBar: { display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', marginBottom: '10px', flexWrap: 'wrap' },
  stateDot: { width: '8px', height: '8px', borderRadius: '50%' },
  tinyTag: { color: C.textDim, fontSize: '9.5px', fontWeight: '800', letterSpacing: '0.8px' },
  playerName: { color: C.text, fontSize: '13.5px', fontWeight: '800', maxWidth: '120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  vsTag: { color: C.textFaint, fontSize: '11px', fontWeight: '800' },
  boardWrap: { position: 'relative', maxWidth: '480px', width: '100%', margin: '0 auto', cursor: 'pointer' },
  watermark: { position: 'absolute', bottom: '6px', right: '9px', color: 'rgba(255,255,255,0.28)', fontSize: '10px', fontWeight: '800', fontFamily: FONT.display, letterSpacing: '0.5px', pointerEvents: 'none' },
  endedOverlay: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(10,13,12,0.55)', backdropFilter: 'blur(2px)', borderRadius: '8px', zIndex: 20 },
  endedText: { color: '#fff', fontFamily: FONT.display, fontSize: '20px', fontWeight: '800' },
  endedSub: { color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginTop: '4px' },
  floatLayer: { position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible', zIndex: 30 },
  rail: { position: 'absolute', right: '10px', bottom: '112px', zIndex: 40, display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' },
  railBtn: { width: '48px', minHeight: '48px', borderRadius: '16px', cursor: 'pointer', background: 'rgba(20,24,22,0.7)', border: `1px solid ${C.lineStrong}`, backdropFilter: 'blur(6px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', fontFamily: 'inherit', padding: '7px 0' },
  railCount: { color: '#fff', fontSize: '10px', fontWeight: '800' },
  railLabel: { color: 'rgba(255,255,255,0.85)', fontSize: '8.5px', fontWeight: '800' },
  sheet: { position: 'absolute', right: '68px', bottom: '166px', zIndex: 50, background: 'linear-gradient(180deg, #262D2A, #1E2421)', border: `1px solid ${C.lineStrong}`, borderRadius: '16px', padding: '14px', width: '210px', boxShadow: '0 16px 40px rgba(0,0,0,0.55)' },
  sheetTitle: { color: C.text, fontSize: '12.5px', fontWeight: '800', marginBottom: '9px' },
  giftTargets: { display: 'flex', gap: '6px', marginBottom: '9px' },
  giftTarget: { flex: 1, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.line}`, color: C.textDim, fontSize: '11px', fontWeight: '700', padding: '8px 6px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  giftTargetActive: { borderColor: C.accent, color: C.accent, background: 'rgba(52,211,153,0.1)' },
  giftAmounts: { display: 'flex', gap: '6px' },
  giftAmount: { flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.35)', color: '#FBBF24', fontSize: '12px', fontWeight: '800', padding: '9px 0', borderRadius: '9px', cursor: 'pointer', fontFamily: 'inherit' },
  giftBalance: { color: C.textFaint, fontSize: '10.5px', fontWeight: '700', marginTop: '9px', textAlign: 'center' },
  commentsOverlay: { position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-end' },
  commentsSheet: { width: '100%', maxHeight: '62vh', background: 'linear-gradient(180deg, #2A302D, #1B201E)', borderRadius: '22px 22px 0 0', border: `1px solid ${C.lineStrong}`, borderBottom: 'none', padding: '10px 16px 20px', display: 'flex', flexDirection: 'column' },
  commentsHandle: { width: '40px', height: '4px', borderRadius: '99px', background: 'rgba(255,255,255,0.25)', margin: '0 auto 12px' },
  commentsTitle: { color: C.text, fontSize: '14px', fontWeight: '800', marginBottom: '12px' },
  commentsList: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '11px', marginBottom: '12px' },
  commentsEmpty: { color: C.textFaint, fontSize: '12.5px', textAlign: 'center', padding: '20px 0' },
  commentRow: { display: 'flex', flexDirection: 'column', gap: '2px' },
  commentBy: { color: C.accent, fontSize: '11px', fontWeight: '800' },
  commentText: { color: C.text, fontSize: '13px', lineHeight: 1.4 },
  commentInputRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  commentInput: { flex: 1, background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.lineStrong}`, borderRadius: '12px', padding: '12px 14px', color: C.text, fontSize: '13.5px', fontFamily: 'inherit', outline: 'none' },
  commentSend: { width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  note: { position: 'absolute', left: '50%', bottom: '86px', transform: 'translateX(-50%)', zIndex: 55, background: '#D9F5E7', color: '#06281A', fontSize: '12px', fontWeight: '800', padding: '10px 18px', borderRadius: '12px', boxShadow: '0 10px 26px rgba(0,0,0,0.45)', whiteSpace: 'nowrap' },
  centerBox: { height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  emptyCard: { textAlign: 'center', background: 'rgba(255,255,255,0.04)', border: `1px dashed ${C.lineStrong}`, borderRadius: '18px', padding: '34px 26px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' },
  emptyText: { color: C.textDim, fontSize: '13px' },
  startBtn: { background: 'linear-gradient(135deg, #0E9F6E, #34D399)', color: '#06281A', border: 'none', padding: '12px 24px', borderRadius: '11px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
};
